import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useCreateCalendarEvent, CalendarEventType, CalendarVisibility } from "@/hooks/useCalendarEvents";
import { useProjects } from "@/hooks/useProjects";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultDate?: Date;
}

const EVENT_TYPES: { value: CalendarEventType; label: string }[] = [
  { value: "milestone", label: "Milestone" },
  { value: "wo_schedule", label: "Work Order" },
  { value: "draw_deadline", label: "Draw Deadline" },
  { value: "inspection", label: "Inspection" },
  { value: "permit", label: "Permit" },
  { value: "meeting", label: "Meeting" },
  { value: "personal", label: "Personal" },
];

export const NewEventDialog = ({ open, onOpenChange, defaultDate }: Props) => {
  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState<CalendarEventType>("milestone");
  const [projectId, setProjectId] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | undefined>(defaultDate || new Date());
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [visibility, setVisibility] = useState<CalendarVisibility>("team");
  const [description, setDescription] = useState("");

  const { data: projects } = useProjects();
  const createEvent = useCreateCalendarEvent();

  const handleSave = () => {
    if (!title.trim() || !startDate) {
      toast.error("Title and start date are required");
      return;
    }
    createEvent.mutate(
      {
        title: title.trim(),
        event_type: eventType,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: endDate ? format(endDate, "yyyy-MM-dd") : undefined,
        start_time: !allDay && startTime ? startTime + ":00" : undefined,
        end_time: !allDay && endTime ? endTime + ":00" : undefined,
        all_day: allDay,
        visibility,
        description: description.trim() || undefined,
        project_id: projectId || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Event created");
          onOpenChange(false);
          resetForm();
        },
        onError: (e) => toast.error(e.message),
      }
    );
  };

  const resetForm = () => {
    setTitle("");
    setEventType("milestone");
    setProjectId("");
    setStartDate(new Date());
    setEndDate(undefined);
    setAllDay(true);
    setStartTime("");
    setEndTime("");
    setVisibility("team");
    setDescription("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={eventType} onValueChange={(v) => setEventType(v as CalendarEventType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {(projects || []).map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "MMM d, yyyy") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "MMM d, yyyy") : "Same day"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={allDay} onCheckedChange={(c) => setAllDay(!!c)} />
              All day
            </label>
            {!allDay && (
              <div className="flex items-center gap-2">
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-28" />
                <span className="text-muted-foreground text-sm">to</span>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-28" />
              </div>
            )}
          </div>

          <div>
            <Label>Visibility</Label>
            <RadioGroup value={visibility} onValueChange={(v) => setVisibility(v as CalendarVisibility)} className="flex gap-4 mt-1">
              {(["team", "personal", "project", "company"] as const).map((v) => (
                <label key={v} className="flex items-center gap-1.5 text-sm capitalize cursor-pointer">
                  <RadioGroupItem value={v} />
                  {v}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional notes…" rows={2} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createEvent.isPending}>
              {createEvent.isPending ? "Saving…" : "Save Event →"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
